import IconLock from '@assets/icons/lock.svg?react';
import { useTheme } from '@mui/material';
import {
 gridFilterModelSelector,
 useGridApiContext,
 useGridSelector,
} from '@mui/x-data-grid-premium';
import { MutableRefObject, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useConfirmationDialog } from '../../../../contexts/useConfirmationDialog';
import { usePage } from '../../../../contexts/usePageContext';
import { ColPresetType } from '../../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { getBaseDomain } from '../../../../redux/pageDomain';
import reduxSlicesList from '../../../../redux/reduxSlicesList';
import {
 approveResultStart,
 deleteItemsStart,
} from '../../../../redux/results/resultsSlice';
import { RootState } from '../../../../redux/store';
import { downloadBase64File } from '../../../../utils/base64File';
import useModalConf from '../../ModalConf/useModalConf';
import { CustomGridApi } from '../types';
import { prepareFilters } from '../utils';
import { deleteItemStart } from '@/redux/settings/settingsSlice';

const useGridToolbar = () => {
 const dispatch = useAppDispatch();
 const { t } = useTranslation();
 const th = useTheme();
 const { pageName } = usePage();
 const { openDialog: openConfirmationDialog } = useConfirmationDialog();

 const slice = reduxSlicesList[pageName as keyof typeof reduxSlicesList];
 const { structure, success, loading, excel } = useAppSelector(
  (state: RootState) => state[getBaseDomain(pageName)],
 ) as any;

 const { currentColumnsPreset, tempColumnsPreset } = useSelector(
  (state: RootState) => state.clientConfiguration,
 );

 const apiRef = useGridApiContext() as MutableRefObject<CustomGridApi>;
 const apiCurrent = apiRef.current;

 const [selectedRows, setSelectedRows] = useState<Array<number>>();
 const rowCount = apiCurrent.getRowsCount();

 const gridSelector = useGridSelector(apiRef, gridFilterModelSelector);

 const filtersCount = useMemo(() => {
  const filterItems = gridSelector.items;
  const uniqueFilters = new Set();

  for (const i of filterItems) {
   if (i.field !== undefined) {
    uniqueFilters.add(i.field);
   }
  }

  return uniqueFilters.size;
 }, [gridSelector.items]);

 const isCompactDensity = !!currentColumnsPreset?.[pageName]?.isPro;
 const currentPresetForPage = (tempColumnsPreset?.[pageName] ||
  currentColumnsPreset?.[pageName]) as ColPresetType | null;

 const { hasUnappliedPresetChanges, handleRevert, handleSave } =
  useModalConf();

 const handleGetExcel = () => {
  const filters = prepareFilters(
   apiRef.current.state.filter.filterModel.items,
   structure,
  );
  dispatch(slice.getTableExcelStart({ table: pageName, ...filters }));
 };

 const handleDeleteItems = () => {
  openConfirmationDialog({
   title: t('global.delete'),
   message: t('global.deleteMessage'),
   onConfirm: () => {
    selectedRows?.forEach((id) => {
     dispatch(deleteItemStart({ table: pageName, id }));
    });
   },
  });
 };

 const handleBlockResults = () => {
  openConfirmationDialog({
   title: t('global.block'),
   message: t('global.blockMessage'),
   buttonText: t('global.block'),
   buttonProps: {
    color: 'primary',
    variant: 'contained',
    endIcon: <IconLock style={{ color: 'white' }} />,
   },
   onConfirm: () => {
    dispatch(approveResultStart(selectedRows));
   },
  });
 };

 useEffect(() => {
  if (success?.getTableExcel) {
   if (excel?.file && excel?.type && excel?.extension && excel?.name) {
    downloadBase64File(excel?.file, excel?.name, excel?.type, excel?.extension);
   }
  }
 }, [success]);

 useEffect(() => {
  const handleSelectionChange = () => {
   const rows = apiCurrent.getSelectedRows();
   setSelectedRows(
    Array.from(rows.values())
     .filter((data) => data?.Id)
     .map((data) => data.Id),
   );
  };

  const unsubscribe = apiCurrent.subscribeEvent(
   'rowSelectionChange',
   handleSelectionChange,
  );

  return () => unsubscribe();
 }, [apiRef]);

 return {
  t,
  th,
  pageName,
  loading,
  apiCurrent,
  rowCount,
  selectedRows,
  filtersCount,
  isCompactDensity,
  currentPresetForPage,
  hasUnappliedPresetChanges,
  handleGetExcel,
  handleDeleteItems,
  handleBlockResults,
  handleResetPreset: handleRevert,
  handleSavePreset: handleSave,
 };
};

export default useGridToolbar;
